import { Component, ReactNode } from 'react'
import ErrorLayout, { SuperTitle, ButtonWrapper } from 'components/layouts/ErrorLayout'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import Button, { ButtonProps } from 'components/features/pfm/Button/Button'
import { USER_BLOCKED_TITLE, USER_BLOCKED_TEXT } from 'components/atoms/Errors'
import { UnexpectedErrorType, UnexpectedInternalError } from 'interfaces/errors'
import { SUPPORT_EMAIL } from 'constants/email'
import { DASHBOARD_URL } from 'tangle-shared'
import { isAjaxError } from 'helpers/errorHelpers'
import { IS_STATUS_APP } from 'helpers/userAgentHelpers'
import LoginLink from 'components/atoms/LoginLink'
import { EmailLink } from 'components/features/pfm/EmailLink/EmailLink'

export type ErrorPageStateProps = {
  errorType: UnexpectedErrorType
  internalError?: UnexpectedInternalError
  isAuthorized: boolean
  url: string
}

export type ErrorPageDispatchProps = {
  onLogOut: () => void
  onReload: (url?: string) => void
  onUpdate: () => void
}

type ErrorPageProps = ErrorPageStateProps & ErrorPageDispatchProps

const REQUEST_ERROR_TITLE = 'Sorry, we had a problem with your request.'

const DEFAULT_ERROR_TEXT = (
  <>
    If you continue to encounter this error, please contact us at
    <br />
    <EmailLink email={ SUPPORT_EMAIL } />
  </>
)

const getNotFoundText = (type: 'user' | 'page' | 'tag' | 'post') => (
  <>
    Unfortunately, this { type } does not exist.
    Please check the address (URL) that you entered
    <br /><br />
    or
  </>
)

const TEXTS: Record<UnexpectedErrorType, ReactNode> = {
  'server-error': DEFAULT_ERROR_TEXT,
  'request-error': DEFAULT_ERROR_TEXT,
  'runtime-error': DEFAULT_ERROR_TEXT,
  // see also CREDIT_USER_BLOCKED
  'user-blocked': USER_BLOCKED_TEXT,
  'page-not-found': getNotFoundText('page'),
  'user-not-found': getNotFoundText('user'),
  'tag-not-found': getNotFoundText('tag'),
  'post-not-found': getNotFoundText('post'),
}

// these solves many issues related to soft links to another pages (without hard reload)

// I am against this decision, sinceraly your, @rendalf
class ErrorPage extends Component<ErrorPageProps, {}> {
  render () {
    const { isAuthorized, errorType } = this.props

    return (
      <ErrorLayout
        hasLoginButton={ !isAuthorized && errorType !== 'user-blocked' }
        title={ this.renderErrorCode() }
        subtitle={ this.renderTitle() }
        text={ TEXTS[errorType] }
        buttons={ this.renderButtons() }
      />
    )
  }


  renderErrorCode () {
    const { errorType, internalError } = this.props

    switch (errorType) {
      case 'post-not-found':
      case 'page-not-found':
      case 'user-not-found':
      case 'tag-not-found':
        return <SuperTitle>404</SuperTitle>

      case 'server-error':
        return (
          <SuperTitle>
            { internalError && isAjaxError(internalError) ? internalError.status : 500 }
          </SuperTitle>
        )

      default:
        return null
    }
  }

  renderTitle () {
    switch (this.props.errorType) {
      case 'user-blocked':
        return USER_BLOCKED_TITLE

      case 'user-not-found':
        return 'User Not Found'

      case 'tag-not-found':
        return 'Tag Not Found'

      case 'page-not-found':
        return 'Page Not Found'

      case 'post-not-found':
        return 'Post Not Found'

      default:
        return REQUEST_ERROR_TITLE
    }
  }

  renderButtons () {
    const buttonChildren: ReactNode[] = []
    const { isAuthorized, url, errorType } = this.props

    switch (errorType) {

      case 'user-blocked':
        if (IS_STATUS_APP) {
          buttonChildren.push(this.renderLogInButton())
        } else {
          buttonChildren.push(this.renderLogOutButton())
        }
        break

      case 'user-not-found':
      case 'tag-not-found':
      case 'post-not-found':
        if (isAuthorized) {
          buttonChildren.push(this.renderFeedButton())
        } else {
          buttonChildren.push(this.renderHomepageButton())
        }
        break

      case 'page-not-found':
        if (isAuthorized) {
          buttonChildren.push(this.renderDashboardButton(undefined))
        } else if (IS_STATUS_APP) {
          buttonChildren.push(this.renderLogInButton())
        } else {
          buttonChildren.push(this.renderHomepageButton())
        }
        break

      default:
        buttonChildren.push(this.renderReloadButton())
        if (isAuthorized && url !== DASHBOARD_URL) {
          buttonChildren.push(this.renderDashboardButton('hollow'))
        }
        break

    }

    return buttonChildren.map((buttonChild, buttonIndex) => {
      return (
        <ButtonWrapper key={ buttonIndex }>
          { buttonChild }
        </ButtonWrapper>
      )
    })
  }

  renderLogInButton () {
    return (
      <LoginLink>
        <Button fullWidth qaMarker='error-page-log-in-button'>
          Log In
        </Button>
      </LoginLink>
    )
  }

  renderLogOutButton () {
    const { onLogOut } = this.props

    return (
      <Button fullWidth onClick={ onLogOut } qaMarker='error-page-return-home-button'>
        Return to Homepage
      </Button>
    )
  }

  renderDashboardButton (style: ButtonProps['style']) {
    return (
      <NavigationLink to='dashboard'>
        <Button fullWidth style={ style }>
          Go to your Dashboard
        </Button>
      </NavigationLink>
    )
  }

  renderHomepageButton () {
    return (
      <NavigationLink to='home'>
        <Button fullWidth>
          Go to Homepage
        </Button>
      </NavigationLink>
    )
  }

  renderFeedButton () {
    return (
      <NavigationLink to='feedposts'>
        <Button fullWidth>
          Go to the Advice Feed
        </Button>
      </NavigationLink>
    )
  }

  renderReloadButton () {
    return (
      <Button fullWidth onClick={ this.handleClickReload }>
        Reload the page
      </Button>
    )
  }

  componentDidMount () {
    document.addEventListener('click', this.handleErrorAnchorClick, true)
  }

  componentWillUnmount () {
    document.removeEventListener('click', this.handleErrorAnchorClick, true)
  }

  private handleClickReload = () => {
    this.props.onReload()
  }

  /*
   * All the navigation from the error page should use hard reload.
   * Therefore we catch any click to a link, determine it's `href` and do hard reload to this url.
   */
  private handleErrorAnchorClick = (event: MouseEvent) => {
    if (!event.target) {
      return
    }

    const closestLink = (event.target as HTMLElement).closest('a[href]')
    if (!closestLink) {
      return
    }

    const href = closestLink.getAttribute('href')
    if (!href) {
      return
    }

    event.stopPropagation()
    event.preventDefault()
    this.props.onReload(href)
  }
}

export default ErrorPage
